import { useState, useRef, useEffect } from 'react'
import DailySchedule from './DailySchedule'
import { useTime } from '@/contexts/TimeContext'
import { playMedLogged } from '@/utils/feedback'

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const JS_DAY_TO_LABEL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function formatTime(time24) {
  const [h, m] = time24.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 || 12
  return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`
}

function formatEndTime(startTime, durationMin) {
  const [h, m] = startTime.split(':').map(Number)
  const totalMin = h * 60 + m + durationMin
  const endH = Math.floor(totalMin / 60) % 24
  const endM = totalMin % 60
  return formatTime(`${endH.toString().padStart(2, '0')}:${endM.toString().padStart(2, '0')}`)
}

function getCurrentWeekDates() {
  const today = new Date()
  const day = today.getDay()
  const mondayOffset = day === 0 ? -6 : 1 - day
  const monday = new Date(today)
  monday.setDate(today.getDate() + mondayOffset)

  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday)
    d.setDate(monday.getDate() + i)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  })
}

function getDayStatus(dateStr, medications, medLog, todayStr) {
  if (medications.length === 0) return 'none'
  if (dateStr > todayStr) return 'future'

  const dayLog = medLog[dateStr] || {}
  const allTaken = medications.every((m) => dayLog[m.id])

  if (allTaken) return 'complete'
  if (dateStr === todayStr) return 'today-incomplete'
  return 'missed'
}

function calculateStreak(medLog, medications, todayStr) {
  if (medications.length === 0) return 0
  let streak = 0
  const d = new Date(todayStr + 'T12:00:00')
  // Check today first — if complete, count it
  for (let i = 0; i < 365; i++) {
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    const dayLog = medLog[dateStr] || {}
    const allTaken = medications.every((m) => dayLog[m.id])
    if (allTaken) {
      streak++
    } else if (i === 0) {
      // Today incomplete — skip but keep checking yesterday
    } else {
      break
    }
    d.setDate(d.getDate() - 1)
  }
  return streak
}

function Dashboard({ terms = [], tasks = [], classes = [], medications = [], medLog = {}, onToggleMedLog, onToggleSubtask, onNavigate, displayName = '', defaultDayStart, defaultDayEnd, savedSchedule, onSaveSchedule, loadScheduleByDate }) {
  const { now: getNow } = useTime()
  const [selectedDay, setSelectedDay] = useState(null)
  const [pastDayPending, setPastDayPending] = useState(null)
  const [streakBounce, setStreakBounce] = useState(false)
  const prevStreakRef = useRef(null)

  const currentTime = getNow()
  const todayStr = `${currentTime.getFullYear()}-${String(currentTime.getMonth() + 1).padStart(2, '0')}-${String(currentTime.getDate()).padStart(2, '0')}`
  const streak = calculateStreak(medLog, medications, todayStr)

  useEffect(() => {
    if (prevStreakRef.current !== null && streak > prevStreakRef.current) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: trigger bounce animation on streak increase
      setStreakBounce(true)
      playMedLogged()
    }
    prevStreakRef.current = streak
  }, [streak])

  const greeting = displayName ? `Welcome, ${displayName}!` : 'Welcome!'
  const hasTerms = terms.length > 0
  const hasSchedule = classes.length > 0

  // No terms created — show only the "create a term" prompt
  if (!hasTerms) {
    return (
      <div className="page dashboard">
        <h2 className="page-title">{greeting}</h2>
        <button className="onboarding-box" onClick={() => onNavigate('terms')}>
          <span className="onboarding-box-icon">+</span>
          <span className="onboarding-box-text">Create a new term to get started</span>
        </button>
      </div>
    )
  }

  // Term exists but no class schedule — prompt to create schedule
  if (!hasSchedule) {
    return (
      <div className="page dashboard">
        <button className="onboarding-box" onClick={() => onNavigate('schedule')}>
          <span className="onboarding-box-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </span>
          <span className="onboarding-box-text">Create a class schedule for your term</span>
        </button>
      </div>
    )
  }

  const todayDayLabel = JS_DAY_TO_LABEL[currentTime.getDay()]
  const todayClasses = classes
    .filter((c) => c.days.includes(todayDayLabel))
    .sort((a, b) => a.startTime.localeCompare(b.startTime))
  const weekDates = getCurrentWeekDates()

  const upcomingTasks = tasks.filter((t) => {
    const isComplete = t.subtasks.length > 0 && t.subtasks.every((st) => st.done)
    return !isComplete && t.dueDate <= todayStr
  })

  const activeTasks = tasks.filter((t) => {
    const isComplete = t.subtasks.length > 0 && t.subtasks.every((st) => st.done)
    return !isComplete && t.dueDate > todayStr
  })

  function formatDialogDate(dateStr) {
    const date = new Date(dateStr + 'T00:00:00')
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    })
  }

  return (
    <div className="page dashboard">
      {medications.length > 0 && (
        <div className="med-week-box">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span className="med-week-box-label">Medications</span>
            {streak > 0 && (
              <div
                className={`med-streak-counter${streakBounce ? ' med-streak-counter--bounce' : ''}`}
                onAnimationEnd={() => setStreakBounce(false)}
              >
                <span className="med-streak-number">{streak}</span>
                <span className="med-streak-label">day streak</span>
              </div>
            )}
          </div>
          <div className="med-week-strip">
            {weekDates.map((dateStr, i) => {
              const status = getDayStatus(dateStr, medications, medLog, todayStr)
              const dateNum = new Date(dateStr + 'T00:00:00').getDate()
              const isToday = dateStr === todayStr
              const isPast = dateStr < todayStr

              const isFuture = dateStr > todayStr

              return (
                <button
                  key={dateStr}
                  className={`med-week-cell ${isToday ? 'med-week-cell--today' : ''}${isFuture ? ' med-week-cell--disabled' : ''}${isPast ? ' med-week-cell--past' : ''}`}
                  disabled={isFuture}
                  onClick={() => {
                    if (isFuture) return
                    if (isPast) {
                      setPastDayPending(dateStr)
                      return
                    }
                    if (medications.length === 1) {
                      onToggleMedLog(dateStr, medications[0].id)
                    } else {
                      setSelectedDay(dateStr)
                    }
                  }}
                >
                  <span className="med-week-day">{DAY_LABELS[i]}</span>
                  <span className="med-week-date">{dateNum}</span>
                  <span className={`med-week-status med-week-status--${status}`}>
                    {status === 'complete' && '✓'}
                    {status === 'missed' && '✕'}
                    {(status === 'future' || status === 'today-incomplete') && '○'}
                    {status === 'none' && '–'}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {todayClasses.length > 0 && (
        <section className="dashboard-section dashboard-today-schedule">
          <h3>Today's Class Schedule</h3>
          <ul className="dashboard-task-list">
            {todayClasses.map((cls, i) => (
              <li
                key={cls.id}
                className="dashboard-task-item dashboard-task-item--link stagger-item"
                style={{ animationDelay: `${Math.min(i, 8) * 60}ms` }}
                onClick={() => onNavigate('schedule')}
              >
                <span className="dashboard-class-time">
                  {formatTime(cls.startTime)} – {formatEndTime(cls.startTime, cls.duration)}
                </span>
                <span className="dashboard-task-title">{cls.name}</span>
                {cls.location && (
                  <span className="dashboard-class-location">{cls.location}</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <DailySchedule
        classes={classes}
        tasks={tasks}
        onToggleSubtask={onToggleSubtask}
        onNavigate={onNavigate}
        defaultDayStart={defaultDayStart}
        defaultDayEnd={defaultDayEnd}
        savedSchedule={savedSchedule}
        onSaveSchedule={onSaveSchedule}
        loadScheduleByDate={loadScheduleByDate}
      />

      {upcomingTasks.length > 0 && (
        <section className="dashboard-section dashboard-section--urgent">
          <h3>Due Today / Overdue</h3>
          <ul className="dashboard-task-list">
            {upcomingTasks.map((t, i) => {
              const done = t.subtasks.filter((st) => st.done).length
              const totalTime = t.subtasks.reduce((s, st) => s + (st.timeEstimate || 0), 0)
              const doneTime = t.subtasks.filter((st) => st.done).reduce((s, st) => s + (st.timeEstimate || 0), 0)
              const timePct = totalTime > 0 ? Math.round((doneTime / totalTime) * 100) : 0
              return (
                <li key={t.id} className="dashboard-task-item dashboard-task-item--link stagger-item" style={{ animationDelay: `${Math.min(i, 8) * 60}ms` }} onClick={() => onNavigate('tasks', t.id)}>
                  <span className={`priority-dot priority-dot--${t.priority}`} />
                  <span className="dashboard-task-title">{t.title}</span>
                  <span className="dashboard-task-progress">
                    {done}/{t.subtasks.length} · {timePct}%
                  </span>
                </li>
              )
            })}
          </ul>
        </section>
      )}

      {activeTasks.length > 0 && (
        <section className="dashboard-section">
          <h3>Coming Up</h3>
          <ul className="dashboard-task-list">
            {activeTasks.map((t, i) => {
              const done = t.subtasks.filter((st) => st.done).length
              const totalTime = t.subtasks.reduce((s, st) => s + (st.timeEstimate || 0), 0)
              const doneTime = t.subtasks.filter((st) => st.done).reduce((s, st) => s + (st.timeEstimate || 0), 0)
              const timePct = totalTime > 0 ? Math.round((doneTime / totalTime) * 100) : 0
              const date = new Date(t.dueDate + 'T00:00:00')
              const label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
              return (
                <li key={t.id} className="dashboard-task-item dashboard-task-item--link stagger-item" style={{ animationDelay: `${Math.min(i, 8) * 60}ms` }} onClick={() => onNavigate('tasks', t.id)}>
                  <span className={`priority-dot priority-dot--${t.priority}`} />
                  <span className="dashboard-task-title">{t.title}</span>
                  <span className="dashboard-task-due">{label}</span>
                  <span className="dashboard-task-progress">
                    {done}/{t.subtasks.length} · {timePct}%
                  </span>
                </li>
              )
            })}
          </ul>
        </section>
      )}

      {tasks.length === 0 && (
        <button className="onboarding-box" onClick={() => onNavigate('tasks')}>
          <span className="onboarding-box-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="18" x2="12" y2="12" />
              <line x1="9" y1="15" x2="15" y2="15" />
            </svg>
          </span>
          <span className="onboarding-box-text">
            {displayName ? `${displayName}, create` : 'Create'} your first task — we'll break it into manageable steps
          </span>
        </button>
      )}

      {medications.length === 0 && (
        <button className="onboarding-box" onClick={() => onNavigate('medication')}>
          <span className="onboarding-box-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5" />
              <rect x="9" y="0" width="6" height="4" rx="1" />
              <line x1="12" y1="11" x2="12" y2="17" />
              <line x1="9" y1="14" x2="15" y2="14" />
            </svg>
          </span>
          <span className="onboarding-box-text">
            {displayName ? `${displayName}, add` : 'Add'} your medications to start tracking adherence
          </span>
        </button>
      )}

      <button className="onboarding-box onboarding-box--action" onClick={() => onNavigate('tasks')}>
        <span className="onboarding-box-icon">+</span>
        <span className="onboarding-box-text">Create a new task</span>
      </button>

      {pastDayPending && (
        <div className="modal-overlay" onClick={() => setPastDayPending(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Change Past Entry</h3>
              <button className="btn-icon" onClick={() => setPastDayPending(null)}>✕</button>
            </div>
            <p style={{ padding: '0 1rem 1rem', color: 'var(--text-secondary)', margin: 0 }}>
              Do you want to update the medication log for {formatDialogDate(pastDayPending)}?
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', padding: '0 1rem 1rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setPastDayPending(null)}>No</button>
              <button className="btn btn-primary" onClick={() => {
                const day = pastDayPending
                setPastDayPending(null)
                if (medications.length === 1) {
                  onToggleMedLog(day, medications[0].id)
                } else {
                  setSelectedDay(day)
                }
              }}>Yes</button>
            </div>
          </div>
        </div>
      )}

      {selectedDay && (
        <div className="modal-overlay" onClick={() => setSelectedDay(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{formatDialogDate(selectedDay)}</h3>
              <button
                className="btn-icon"
                onClick={() => setSelectedDay(null)}
              >
                ✕
              </button>
            </div>
            {medications.length > 0 ? (
              <ul className="med-dialog-list">
                {medications.map((med) => {
                  const taken = medLog[selectedDay]?.[med.id] || false
                  return (
                    <li key={med.id} className="med-dialog-item">
                      <label className="med-dialog-label">
                        <input
                          type="checkbox"
                          className="subtask-checkbox"
                          checked={taken}
                          onChange={() => onToggleMedLog(selectedDay, med.id)}
                        />
                        <span className={taken ? 'med-dialog-name med-dialog-name--taken' : 'med-dialog-name'}>
                          {med.name}
                        </span>
                        <span className="med-item-freq">{med.timeOfDay}</span>
                      </label>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <p className="placeholder-text" style={{ padding: '1rem' }}>
                No medications to track.
              </p>
            )}
          </div>
        </div>
      )}

    </div>
  )
}

export default Dashboard
